import { useEffect, useState } from "react";
import "./CareerProgram.css";
import { Card, message, Input, Spin, Empty, Pagination, Tag } from "antd";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { BaseUrl } from "../Constants/Constants";
import { useStateValue } from "../../Reducer/reducer";
import WorkIcon from "@mui/icons-material/Work";
import SearchIcon from "@mui/icons-material/Search";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

const CareerProgramGroup = () => {
  const navigate = useNavigate();

  const [{ UserID, companyId }, dispatch] = useStateValue();

  const [courses, setCourses] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchValue, setSearchValue] = useState("");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);

  console.log(companyId, "companyId");

  useEffect(() => {
    if (UserID != undefined || UserID != "") {
      setLoading(true);

      let body = {
        userId: UserID,
      };

      axios
        .post(`${BaseUrl}career-course-mapping/getAllCourses`, body, {
          withCredentials: true,
        })
        .then((res) => {
          if (res.data.statuscode === 200) {
            let mapped = res.data.data.map((course) => ({
              course_id: course.id,
              course_title: course.title,
              group_id: course.group_id,
              group_name: course.group_name || "Others",
              group_learning: course.group_learning === true,
              imageUrl: "/icons/CourseIcons.png",
            }));

            setCourses(mapped);
            setFilteredCourses(mapped);
            setLoading(false);
          } else {
            setCourses([]);
            setFilteredCourses([]);
            setLoading(false);
          }
        })
        .catch((err) => {
          console.log(err.message);
          setLoading(false);
          message.error("Please login again to continue!");
          navigate("/login");
          setCourses([]);
          setFilteredCourses([]);
        });
    } else {
      message.error("Please login again to continue!");
      navigate("/login");
    }
  }, []);

  // Step 1: Create a master group map from all courses
  const masterGroupMap = (() => {
    const map = new Map();
    courses.forEach((course) => {
      if (!map.has(course.group_id)) {
        map.set(course.group_id, {
          group_id: course.group_id,
          group_name: course.group_name,
          imageUrl: course.imageUrl,
          course_count: 1,
          course_ids: [course.course_id],
          allCompleted: course.group_learning === true,
          isLocked: true,
        });
      } else {
        const group = map.get(course.group_id);
        group.course_count += 1;
        group.course_ids.push(course.course_id);
        if (!course.group_learning) group.allCompleted = false;
      }
    });

    const groups = Array.from(map.values());

    // Unlock logic
    groups.forEach((group, index) => {
      group.isLocked = index === 0 ? false : !groups[index - 1].allCompleted;
    });

    return map; // Map of all groups
  })();

  // Step 2: Build the group list for display based on search
  const groupList = (() => {
    if (!searchValue.trim()) {
      return Array.from(masterGroupMap.values());
    }

    const search = searchValue.toLowerCase().trim();

    // Filter courses based on search
    const matchingGroups = courses.filter((course) =>
      course.group_name?.toLowerCase().includes(search)
    );

    const matchingCourses = courses.filter((course) =>
      course.course_title?.toLowerCase().includes(search)
    );

    const displayGroupsMap = new Map();

    if (matchingGroups.length) {
      matchingGroups.forEach((course) => {
        if (masterGroupMap.has(course.group_id)) {
          displayGroupsMap.set(
            course.group_id,
            masterGroupMap.get(course.group_id)
          );
        }
      });
    } else if (matchingCourses.length) {
      matchingCourses.forEach((course) => {
        if (masterGroupMap.has(course.group_id)) {
          displayGroupsMap.set(
            course.group_id,
            masterGroupMap.get(course.group_id)
          );
        }
      });
    }

    return Array.from(displayGroupsMap.values());
  })();

  const handleGroupCard = (group) => {
    const courseIds = courses
      .filter((course) => course.group_id === group.group_id)
      .map((course) => course.course_id);

    navigate(`/career-program/group/${group.group_id}`, {
      state: {
        groupName: group.group_name,
        courseIds,
      },
    });
  };

  const handleSearch = (value) => {
    const search = value.toLowerCase().trim();
    setSearchValue(value);
    setCurrentPage(1);

    if (!search) {
      setFilteredCourses(courses);
      return;
    }

    // Step 1: Check if search matches any group name
    const matchingGroups = courses.filter((course) =>
      course.group_name?.toLowerCase().includes(search)
    );

    // Step 2: Check if search matches any course title
    const matchingCourses = courses.filter((course) =>
      course.course_title?.toLowerCase().includes(search)
    );

    // Combine: if matched group names exist, show unique group cards
    let filtered = [];
    if (matchingGroups.length) {
      // Unique groups only
      const groupMap = new Map();
      matchingGroups.forEach((course) => {
        if (!groupMap.has(course.group_id)) {
          groupMap.set(course.group_id, course);
        }
      });
      filtered = Array.from(groupMap.values());
    } else if (matchingCourses.length) {
      // Show groups that contain matched courses
      const groupMap = new Map();
      matchingCourses.forEach((course) => {
        if (!groupMap.has(course.group_id)) {
          groupMap.set(course.group_id, course);
        }
      });
      filtered = Array.from(groupMap.values());
    } else {
      filtered = [];
    }

    setFilteredCourses(filtered);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Calculate paginated courses
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedCourses = filteredCourses.slice(startIndex, endIndex);

  return (
    <div className="Courses-main">
      {/* Header Section */}
      <div className="coursesHeader">
        <div className="coursesHeaderContent">
          <div className="coursesHeaderIconWrapper">
            <WorkIcon className="coursesHeaderIcon" />
          </div>
          <div>
            <h1 className="coursesMainTitle">Career Program</h1>
            <p className="coursesSubtitle">Explore and enhance your Career</p>
          </div>
        </div>
        <div className="coursesSearchWrapper">
          <Input
            placeholder="Search By Modules or Group Name"
            onChange={(e) => handleSearch(e.target.value)}
            value={searchValue}
            className="coursesSearchInput"
            allowClear
            prefix={<SearchIcon className="searchIconPrefix" />}
            size="large"
          />
        </div>
      </div>

      {loading ? (
        <div className="coursesLoader">
          <Spin size="large" />
        </div>
      ) : (
        <div className="coursesContent">
          {groupList.length === 0 ? (
            <div className="coursesEmptyState">
              <Empty
                description="No courses available to read"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            </div>
          ) : (
            <div className="coursesGrid">
              {groupList.map((group) => (
                <Card
                  key={group.group_id}
                  hoverable={!group.isLocked}
                  className={`courseCard ${
                    group.isLocked ? "disabledCard" : ""
                  }`}
                  onClick={() => !group.isLocked && handleGroupCard(group)}
                  cover={
                    <div className="courseCardCover">
                      <img
                        alt="Group"
                        src={group.imageUrl}
                        className="courseCardImage"
                      />
                    </div>
                  }
                >
                  <div className="courseCardContent">
                    {/* Title + Completed Tag */}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <h3 className="courseCardTitle">
                        {group.group_name.toUpperCase()}
                      </h3>
                    </div>

                    <p style={{ color: "#6b7280", marginTop: "8px" }}>
                      {group.course_count} Modules
                      {group.allCompleted && (
                        <Tag color="green" style={{ marginLeft: "10px" }}>
                          Completed Learning
                        </Tag>
                      )}
                    </p>

                    <div className="courseCardFooter">
                      <span className="courseCardAction">View Programs</span>
                      <AutoAwesomeIcon className="courseCardArrow" />
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CareerProgramGroup;
